import React, { useEffect, useRef, useState } from 'react'
import classes from './carousel.module.css'
import classNames from 'classnames'

const Carousel = ({
    children,
    prevButton = null,
    nextButton = null,
    className = '',
    slideClassName,
    dots = false,
    dotBg,
    dotClass = '',
}) => {
    const [numberOfChildren, setNumberOfChildren] = useState(0)
    const currentSlide = useRef(0)

    //Used separate variable for active dot because changing a ref doesn't trigger a rerender
    const [activeDot, setActiveDot] = useState(0)

    const carouselRef = useRef(null)
    const [addedEvents, setAddedEvents] = useState(false)

    const setCurrentSlide = num => (currentSlide.current = num)

    const scrollToNext = () => {
        const carousel = carouselRef.current
        const children = carousel.childNodes
        let nextSlideDiv
        if (!(currentSlide.current === children.length - 1)) {
            nextSlideDiv = children[currentSlide.current + 1]
            setCurrentSlide(currentSlide.current + 1)
            setActiveDot(currentSlide.current)
        } else {
            nextSlideDiv = children[currentSlide.current]
        }
        carousel.style.left = `-${nextSlideDiv.offsetLeft}px`
        carousel.classList.add(classes.shifting)
    }

    const scrollToPrev = () => {
        const carousel = carouselRef.current
        const children = carousel.childNodes
        let prevSlideDiv
        if (currentSlide.current > 0) {
            prevSlideDiv = children[currentSlide.current - 1]
            setCurrentSlide(currentSlide.current - 1)
            setActiveDot(currentSlide.current)
        } else {
            prevSlideDiv = children[currentSlide.current]
        }
        carousel.style.left = `-${prevSlideDiv.offsetLeft}px`
        carousel.classList.add(classes.shifting)
    }

    const scrollTo = index => {
        carouselRef.current.classList.add(classes.shifting)
        const targetDiv = carouselRef.current.childNodes[index]
        carouselRef.current.style.left = `-${targetDiv.offsetLeft}px`
        setCurrentSlide(index)
        setActiveDot(index)
    }

    useEffect(() => {
        if (prevButton) {
            if (Array.isArray(prevButton.current)) {
                prevButton.current.forEach(button => {
                    button.addEventListener('click', scrollToPrev)
                    button.disabled = true
                })
            } else {
                prevButton.current.disabled = true
                prevButton.current.addEventListener('click', scrollToPrev)
            }
        }

        if (nextButton) {
            if (Array.isArray(nextButton.current)) {
                nextButton.current.forEach(button =>
                    button.addEventListener('click', scrollToNext)
                )
            } else {
                nextButton.current.addEventListener('click', scrollToNext)
            }
        }
    }, [prevButton, nextButton])

    const measureScroll = () => {
        const { current: carousel } = carouselRef

        if (prevButton) {
            const shouldDisablePrevButton = carousel.offsetLeft === 0
            if (Array.isArray(prevButton.current)) {
                prevButton.current.forEach(
                    button => (button.disabled = shouldDisablePrevButton)
                )
            } else {
                prevButton.current.disabled = shouldDisablePrevButton
            }
        }

        if (nextButton) {
            const carouselWrapperRight = carousel.parentElement.getBoundingClientRect()
                .right
            const lastItem = [...carousel.childNodes].pop()
            const lastItemRight = lastItem?.getBoundingClientRect().right
            const shouldDisableNextButton =
                Math.floor(lastItemRight) <= Math.floor(carouselWrapperRight)
            if (Array.isArray(nextButton.current)) {
                nextButton.current.forEach(button => {
                    button.disabled = shouldDisableNextButton
                })
            } else {
                nextButton.current.disabled = shouldDisableNextButton
            }
        }
    }

    const currentX = useRef(0)
    const initialPosition = useRef(0)

    useEffect(() => {
        //Add slide classnames (backwards compatibility: I don't want to edit all those files pls)
        const carousel = carouselRef.current
        const children = carousel.childNodes
        children.forEach(child => child.classList.add(slideClassName))
        setNumberOfChildren(children.length)
    }, [children])

    useEffect(() => {
        if (carouselRef.current && !addedEvents) {
            const carousel = carouselRef.current
            /*
                Had to add it directly because of chrome breaking change
                Read more about it here:
                https://github.com/facebook/react/issues/8968
                https://developers.google.com/web/updates/2017/01/scrolling-intervention
             */
            carousel.addEventListener('touchstart', onTouchStart)
            carousel.addEventListener('touchmove', onTouchMove)
            carousel.addEventListener('touchend', onTouchEnd)
            carousel.addEventListener('transitionend', () => {
                carousel.classList.remove(classes.shifting)
                measureScroll()
            })
            setAddedEvents(true)
        }

        const handleResize = () => {
            const carousel = carouselRef.current
            const carouselWrapperRight = carousel.parentElement.getBoundingClientRect()
                .right
            const lastItem = [...carousel.childNodes].pop()
            const lastItemRight = lastItem?.getBoundingClientRect().right

            let lastOffset
            /*
                If the last item is visible then scroll right to it.
                The logic is if I have moved the carousel 100px right
                And then the last item is visible
                For me to move to the right of the last item I need to find the distance between the last item
                and the carousel wrapper which will be the carousel's right - last item's right
                Let's say it's 50px then I'll move my carousel right 50px
             */

            if (lastItemRight < carouselWrapperRight) {
                lastOffset =
                    -1 *
                    (carousel.offsetLeft +
                        (carouselWrapperRight - lastItemRight))
            } else {
                //Else scroll to the current item
                lastOffset =
                    carousel.childNodes[currentSlide.current]?.offsetLeft
            }
            carousel.style.left =
                carousel.childNodes.length > 0 ? `-${lastOffset}px` : '0px'
            carousel.classList.add(classes.shifting)
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
            setAddedEvents(false)
        }
    }, [carouselRef.current, slideClassName])

    const onTouchStart = e => {
        // e.preventDefault()
        carouselRef.current.classList.remove(classes.shifting)
        currentX.current = e.changedTouches[0].pageX
        initialPosition.current = carouselRef.current.offsetLeft
    }

    const onTouchMove = e => {
        const travelled = currentX.current - e.touches[0].clientX
        currentX.current = e.touches[0].clientX
        carouselRef.current.style.left = `${carouselRef.current.offsetLeft -
            travelled}px`
    }

    const onTouchEnd = e => {
        const distanceTravelled =
            carouselRef.current.offsetLeft - initialPosition.current
        if (Math.abs(distanceTravelled) >= 150) {
            distanceTravelled < 0 ? scrollToNext() : scrollToPrev()
        } else {
            carouselRef.current.classList.add(classes.shifting)
            carouselRef.current.style.left = `${initialPosition.current}px`
        }
    }

    return (
        <div className={classes.carouselWrapper}>
            <div
                className={classNames(classes.carousel, className)}
                ref={carouselRef}
                onScroll={measureScroll}
            >
                {children}
            </div>

            {dots ? (
                <div className={classes.dots}>
                    {[...Array(numberOfChildren)].map((_, idx) => (
                        <button
                            className={classNames(
                                classes.dot,
                                activeDot === idx && classes.dotActive,
                                dotClass
                            )}
                            style={{
                                backgroundColor: dotBg,
                            }}
                            onClick={_ => {
                                scrollTo(idx)
                            }}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    )
}

export default Carousel
